import { Box, Typography } from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";

/**
 * FR-010's graceful not-found for an unrecognized location beneath the Configuration area.
 *
 * **Plain text, never `MessagePage`.** `MessagePage` renders `LoadingIndicator`, which is an
 * Interstate `Spinner` — a *loading* component. Using it for a terminal state showed an unrecognized
 * URL as a spinner that never stops, implying the page was still working when it had already
 * finished, and `LoadingIndicator` also fires `NewRelic.addPageAction("Loading Spinner Finished")` on
 * unmount, so every 404 logged a fictitious load timing. The sibling `ControlCenterIncompletePath`
 * gets this right with plain text; this follows it, using Interstate `Box`/`Typography` rather than
 * its raw `div`/`h1`/`p` (Monorepo rule: no raw HTML elements).
 *
 * **This exists as a component, rather than the element sitting inline in `routes.tsx`, because of
 * module evaluation order.** `routes.tsx` builds its `RouteObject[]` at module scope, so inline copy
 * would resolve the moment the module is evaluated. `index.ts` calls `Locale.setLabelsOnce(LABELS)`
 * in its body but re-exports `./routes` with an `export * from`, and ES import declarations are
 * **hoisted above** statements — so `routes.tsx` runs first and the lookup finds no labels at all.
 * The symptom was a not-found page rendering with no message. Resolving copy inside a render body is
 * the general fix, and Configuration Principle VI's reason for `setLabelsOnce` at module scope in the
 * first place.
 *
 * It lives in the neutral `react/src/components/` surface (Configuration Principle II) rather than
 * under `MyInfo/`: it belongs to the shell's routing, not to any one area, and `usermanager`,
 * `companyinfo`, and `phonemanager` will each want the same treatment.
 */
export const ConfigurationNotFound = () => (
  <Box
    data-testid={"configuration-not-found"}
    sx={{ padding: "24px", textAlign: "center" }}
  >
    {/*
     * A plain paragraph, with no `role` and no live region. The text is present on first paint rather
     * than swapped in later, so there is no change to announce — a live region here would be
     * overclaiming. A heading would overstate it too: this is one sentence, not a page section.
     * Matches `ControlCenterIncompletePath`, which is also role-free.
     */}
    <Typography tag={"p"} variant={"body-md"}>
      {Locale.getText("PAGE_NOT_FOUND") as string}
    </Typography>
  </Box>
);
